import { useState } from "react";
import { Plus, Trash, MapPin, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { LLSelect } from "@/components/ui/ll-select";

const ZONE_KINDS = [
  { value: "yard", label: "Yard / Base" },
  { value: "landfill", label: "Landfill" },
  { value: "customer_site", label: "Customer Site" },
  { value: "restricted", label: "Restricted" },
  { value: "custom", label: "Custom" },
];

const ALERT_OPTS = [
  { value: "entry", label: "On Entry" },
  { value: "exit", label: "On Exit" },
  { value: "both", label: "Entry & Exit" },
  { value: "none", label: "None" },
];

const COLORS = ["#22c55e", "#ff8c00", "#ef4444", "#4682b4", "#a855f7", "#eab308"];

export default function ZonePanel({
  zones,
  onCreate,
  onDelete,
  drawing,
  onStartDraw,
  onCancelDraw,
  pendingPolygon,
}) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: "",
    kind: "custom",
    alert_on: "both",
    color: "#4682b4",
    notes: "",
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!pendingPolygon || pendingPolygon.length < 3) {
      toast.error("Draw a polygon on the map first");
      return;
    }
    try {
      await onCreate({
        ...form,
        polygon: pendingPolygon,
      });
      toast.success(`Zone "${form.name}" created`);
      setOpen(false);
      setForm({ name: "", kind: "custom", alert_on: "both", color: "#4682b4", notes: "" });
    } catch (e) {
      toast.error("Failed to create zone");
    }
  };

  return (
    <div className="bg-black/70 backdrop-blur-xl border border-white/10 w-[280px]" data-testid="zone-panel">
      <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#71717a]">Geofence Zones</div>
          <div className="font-heading font-bold text-sm tracking-tight">{zones.length} Active</div>
        </div>
        {drawing ? (
          <button
            onClick={onCancelDraw}
            className="w-7 h-7 bg-[#ef4444]/15 border border-[#ef4444] hover:bg-[#ef4444] hover:text-white text-[#ef4444] flex items-center justify-center transition-colors"
            data-testid="cancel-draw-btn"
            title="Cancel drawing"
          >
            <X size={14} />
          </button>
        ) : (
          <button
            onClick={() => { setOpen(true); onStartDraw(); }}
            className="w-7 h-7 bg-[#ff8c00] text-black hover:bg-[#e67e00] flex items-center justify-center transition-colors"
            data-testid="draw-zone-btn"
            title="Draw new zone"
          >
            <Plus size={14} weight="bold" />
          </button>
        )}
      </div>

      <div className="max-h-[200px] overflow-y-auto">
        {zones.map((z) => (
          <div key={z.id} className="px-4 py-2 border-b border-white/5 flex items-center gap-2 group" data-testid={`zone-${z.name.replace(/\s+/g, "-")}`}>
            <span className="w-2 h-2" style={{ background: z.color }}></span>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-bold truncate">{z.name}</div>
              <div className="text-[9px] uppercase tracking-wider text-[#71717a] truncate">
                {z.kind.replace("_", " ")} · alert {z.alert_on}
              </div>
            </div>
            <button
              onClick={() => onDelete(z.id)}
              className="opacity-0 group-hover:opacity-100 w-7 h-7 hover:bg-[#ef4444]/20 text-[#a1a1aa] hover:text-[#ef4444] flex items-center justify-center transition-all"
              data-testid={`delete-zone-${z.name.replace(/\s+/g, "-")}`}
            >
              <Trash size={12} />
            </button>
          </div>
        ))}
      </div>

      {drawing && (
        <div className="border-t border-white/10 p-3 bg-[#ff8c00]/5">
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#ff8c00] flex items-center gap-2 mb-1 ll-status-blink">
            <MapPin size={12} weight="fill" /> Drawing Mode
          </div>
          <div className="text-[10px] text-[#a1a1aa]">
            {pendingPolygon && pendingPolygon.length > 0
              ? `${pendingPolygon.length} point${pendingPolygon.length === 1 ? "" : "s"} placed. Click map to add. Double-click to finish.`
              : "Click on the map to start placing zone corners (need 3+)."}
          </div>
        </div>
      )}

      {open && pendingPolygon && pendingPolygon.length >= 3 && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[2000] flex items-center justify-center p-4" data-testid="zone-form-dialog">
          <div className="bg-[#0d0d0d] border border-[#4d4d4d] w-full max-w-md">
            <div className="border-b border-[#2a2a2a] p-4 flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#71717a]">New Geofence</div>
                <h2 className="font-heading font-black text-lg tracking-tight mt-1">Save Zone</h2>
              </div>
              <button onClick={() => { setOpen(false); onCancelDraw(); }} className="w-8 h-8 hover:bg-[#1a1a1a] flex items-center justify-center" data-testid="close-zone-form">
                <X size={18} />
              </button>
            </div>
            <form onSubmit={submit} className="p-5 space-y-4">
              <label className="block">
                <span className="text-[10px] uppercase tracking-[0.25em] text-[#71717a] mb-1.5 block">Name <span className="text-[#ff8c00]">*</span></span>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  className="w-full px-3 py-2 bg-[#1a1a1a] border border-[#2a2a2a] focus:border-[#ff8c00] outline-none text-sm"
                  data-testid="zone-name-input"
                />
              </label>

              <label className="block">
                <span className="text-[10px] uppercase tracking-[0.25em] text-[#71717a] mb-1.5 block">Type</span>
                <LLSelect
                  value={form.kind}
                  onChange={(v) => setForm({ ...form, kind: v })}
                  testId="zone-kind"
                  className="!w-full text-sm normal-case tracking-normal"
                  options={ZONE_KINDS}
                />
              </label>

              <label className="block">
                <span className="text-[10px] uppercase tracking-[0.25em] text-[#71717a] mb-1.5 block">Alert On</span>
                <LLSelect
                  value={form.alert_on}
                  onChange={(v) => setForm({ ...form, alert_on: v })}
                  testId="zone-alert-on"
                  className="!w-full text-sm normal-case tracking-normal"
                  options={ALERT_OPTS}
                />
              </label>

              <div>
                <span className="text-[10px] uppercase tracking-[0.25em] text-[#71717a] mb-1.5 block">Color</span>
                <div className="flex gap-2">
                  {COLORS.map((c) => (
                    <button
                      type="button"
                      key={c}
                      onClick={() => setForm({ ...form, color: c })}
                      className={`w-7 h-7 border-2 transition-all ${form.color === c ? "border-white scale-110" : "border-[#2a2a2a]"}`}
                      style={{ background: c }}
                      data-testid={`color-${c}`}
                    />
                  ))}
                </div>
              </div>

              <div className="text-[10px] text-[#71717a] font-mono">
                {pendingPolygon.length} vertices
              </div>

              <div className="flex items-center gap-2 pt-3 border-t border-[#2a2a2a]">
                <button
                  type="button"
                  onClick={() => { setOpen(false); onCancelDraw(); }}
                  className="flex-1 py-2.5 border border-[#4d4d4d] text-xs uppercase tracking-[0.2em] font-bold hover:bg-[#1a1a1a]"
                  data-testid="zone-form-cancel"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 py-2.5 bg-[#ff8c00] text-black hover:bg-[#e67e00] text-xs uppercase tracking-[0.2em] font-bold transition-colors"
                  data-testid="zone-form-submit"
                >
                  Save Zone
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
